import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
	DashboardsServiceV2,
	WidgetDataDTO,
	WidgetDataSetRequestDTO,
	WidgetDefinitionDTO,
} from 'src/app/core/services/dashboardsv2.service';
import { DashboardWidgetLoadingService } from '../../../dashboard-widget-loading-service';

@Component({
    selector: 'app-widget-stats',
    templateUrl: './widget-stats.component.html',
    styleUrls: ['./widget-stats.component.scss'],
    providers: [DashboardsServiceV2],
})
export class StatsWidgetComponent implements OnChanges, OnDestroy {
    @Input() widgetConfig: WidgetDefinitionDTO;
    @Input() dataSetRequest: WidgetDataSetRequestDTO;
    @Input() loadWidgetData: boolean;

    httpClientService: HttpClient;
    domSanitiserService: DomSanitizer;
    dashboardService: DashboardsServiceV2;

    widgetData: WidgetDataDTO;
    subscription: any;
    isLoaded: boolean = false;

    isPrimaryOnly: boolean;

    public lastLoadedDataSet: WidgetDataSetRequestDTO | undefined = undefined;

    constructor(
        httpClient: HttpClient,
        sanitizer: DomSanitizer,
        dashboardService: DashboardsServiceV2,
        private readonly _dashboardWidgetLoadingService: DashboardWidgetLoadingService
    ) {
        this.httpClientService = httpClient;
        this.domSanitiserService = sanitizer;
        this.dashboardService = dashboardService;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasDataSetChangedSinceLastLoaded =
            JSON.stringify(this.dataSetRequest) !== JSON.stringify(this.lastLoadedDataSet);

        if (changes.dataSetRequest && !changes.dataSetRequest.isFirstChange()) {
            if (this.loadWidgetData && this.widgetConfig.version != 3) {
                this.loadData();
            } else {
                this.markAsLoading();
            }
        }

        if (this.loadWidgetData && hasDataSetChangedSinceLastLoaded && this.widgetConfig.version != 3) {
            this.loadData();
        }

        if (changes.loadWidgetData && !this.isLoaded) {
            if (changes.loadWidgetData.currentValue && this.widgetConfig.version != 3) {
                this.loadData();
            }
            //stop loading unfinished widget
            if (!changes.loadWidgetData.currentValue && this.subscription) {
                this.subscription.unsubscribe();
                this.widgetConfig.loading = false;
                this._dashboardWidgetLoadingService.remove(this.widgetConfig.id.toString());
            }
        }

        // Handle updates for version 3 dashboards where data is pushed directly via widgetConfig.data
        if (changes.widgetConfig && this.widgetConfig.version === 3) {
            if (this.widgetConfig.data) {
                this.widgetData = this.widgetConfig.data;
                this.renderStatsData(); // Call a dedicated rendering method
            }
        }
    }

    loadData() {
        this.widgetConfig.loading = true;
        this.markAsLoading();

        if (this.widgetConfig.version === 3 && this.widgetData) {
            // If version 3 and data is already provided, just render
            this.widgetConfig.loading = false;
            this.markAsLoaded();
            this._updateLastLoadedDataSet();
            this.renderStatsData();
            return;
        }

        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.subscription = this.dashboardService
            .getDataForWidget(this.widgetConfig, this.dataSetRequest)
            .subscribe((data: WidgetDataDTO) => {
                this.widgetData = data;
                this.widgetConfig.loading = false;
                this.markAsLoaded();
                this._updateLastLoadedDataSet();
                this.renderStatsData();
            });
    }

    private renderStatsData(): void {
        this.isPrimaryOnly = this.widgetData.data.some((x) => x.length > 2);
    }

    formatString(label: string): SafeHtml {
        return this.domSanitiserService.bypassSecurityTrustHtml(label.replace('\n', '<br/>'));
    }

    public markAsLoading(): void {
        this.isLoaded = false;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.add(widgetId);
    }

    public markAsLoaded(): void {
        this.isLoaded = true;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.remove(widgetId);
    }

    private _updateLastLoadedDataSet(): void {
        this.lastLoadedDataSet = { ...this.dataSetRequest };
    }
}
